import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckSquare, 
  Clock, 
  Target, 
  Calendar, 
  AlertCircle, 
  CheckCircle, 
  Star, 
  Zap, 
  BookOpen, 
  CreditCard, 
  Home, 
  GraduationCap, 
  FileText, 
  Shield,
  ArrowRight,
  Plus,
  Filter,
  Search,
  TrendingUp,
  Users,
  Award
} from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../contexts/AuthContext';
import { AnimatedCounter } from '../components/ui';

interface ActionItem {
  id: string;
  title: string;
  description: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  estimatedTime: string;
  difficulty: 'easy' | 'medium' | 'hard';
  status: 'pending' | 'in_progress' | 'completed';
  dueDate?: string;
  prerequisites: string[];
  resources: {
    title: string;
    url: string;
    type: 'guide' | 'lesson' | 'tool' | 'external';
  }[];
  completedAt?: string;
  points: number;
  icon: React.ElementType;
}

interface Checklist {
  id: string;
  title: string;
  description: string;
  category: string;
  items: ActionItem[];
  completed: number;
  total: number;
  unlocked: boolean;
  icon: React.ElementType;
  color: string;
}

const NextStepsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [filteredChecklists, setFilteredChecklists] = useState<Checklist[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'checklists' | 'actions'>('checklists');

  useSEO({
    title: 'Next Steps - MyNewStart',
    description: 'Actionable checklists and practical tasks unlocked by your current stage.',
    keywords: ['next steps', 'checklists', 'tasks', 'action items', 'progress'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Dashboard', url: '/dashboard' },
      { name: 'Next Steps', url: '/next-steps' }
    ]
  });

  useEffect(() => {
    loadChecklists();
  }, []);

  useEffect(() => {
    filterContent();
  }, [checklists, searchTerm, selectedCategory, selectedPriority]);

  const loadChecklists = () => {
    // Mock data - in a real app, this would come from your API
    const mockChecklists: Checklist[] = [
      {
        id: '1',
        title: 'Banking & Credit Setup',
        description: 'Essential banking tasks to establish your financial foundation',
        category: 'banking',
        completed: 3,
        total: 8,
        unlocked: true,
        icon: CreditCard,
        color: 'bg-blue-500',
        items: [
          {
            id: '1-1',
            title: 'Open a checking account',
            description: 'Choose a bank and open your first U.S. checking account',
            category: 'banking',
            priority: 'high',
            estimatedTime: '1-2 days',
            difficulty: 'easy',
            status: 'completed',
            prerequisites: [],
            resources: [
              { title: 'Banking Guide', url: '/guides/banking', type: 'guide' },
              { title: 'Bank Comparison Tool', url: '/tools/bank-comparison', type: 'tool' }
            ],
            completedAt: '2024-01-15',
            points: 25,
            icon: CheckCircle
          },
          {
            id: '1-2',
            title: 'Get a debit card',
            description: 'Request and activate your debit card',
            category: 'banking',
            priority: 'high',
            estimatedTime: '3-5 days',
            difficulty: 'easy',
            status: 'completed',
            prerequisites: ['Open a checking account'],
            resources: [
              { title: 'Debit Card Guide', url: '/guides/debit-card', type: 'guide' }
            ],
            completedAt: '2024-01-18',
            points: 15,
            icon: CheckCircle
          },
          {
            id: '1-3',
            title: 'Set up online banking',
            description: 'Create online banking account and download mobile app',
            category: 'banking',
            priority: 'medium',
            estimatedTime: '30 minutes',
            difficulty: 'easy',
            status: 'completed',
            prerequisites: ['Open a checking account'],
            resources: [
              { title: 'Online Banking Setup', url: '/guides/online-banking', type: 'guide' }
            ],
            completedAt: '2024-01-20',
            points: 10,
            icon: CheckCircle
          },
          {
            id: '1-4',
            title: 'Apply for a secured credit card',
            description: 'Build credit history with a secured credit card',
            category: 'banking',
            priority: 'high',
            estimatedTime: '1-2 weeks',
            difficulty: 'medium',
            status: 'in_progress',
            prerequisites: ['Open a checking account'],
            resources: [
              { title: 'Credit Building Guide', url: '/guides/credit-building', type: 'guide' },
              { title: 'Secured Card Comparison', url: '/tools/secured-cards', type: 'tool' }
            ],
            points: 50,
            icon: CreditCard
          },
          {
            id: '1-5',
            title: 'Set up automatic bill payments',
            description: 'Automate recurring payments to avoid late fees',
            category: 'banking',
            priority: 'medium',
            estimatedTime: '1 hour',
            difficulty: 'easy',
            status: 'pending',
            prerequisites: ['Set up online banking'],
            resources: [
              { title: 'Bill Payment Guide', url: '/guides/bill-payments', type: 'guide' }
            ],
            points: 20,
            icon: Calendar
          }
        ]
      },
      {
        id: '2',
        title: 'Housing & Living Setup',
        description: 'Secure stable housing and set up essential services',
        category: 'housing',
        completed: 1,
        total: 6,
        unlocked: true,
        icon: Home,
        color: 'bg-purple-500',
        items: [
          {
            id: '2-1',
            title: 'Find suitable housing',
            description: 'Research and secure appropriate housing for your needs',
            category: 'housing',
            priority: 'high',
            estimatedTime: '2-4 weeks',
            difficulty: 'hard',
            status: 'completed',
            prerequisites: [],
            resources: [
              { title: 'Housing Guide', url: '/guides/housing', type: 'guide' },
              { title: 'Rental Application Template', url: '/tools/rental-application', type: 'tool' }
            ],
            completedAt: '2024-01-10',
            points: 100,
            icon: CheckCircle
          },
          {
            id: '2-2',
            title: 'Set up utilities',
            description: 'Connect electricity, gas, water, and internet services',
            category: 'housing',
            priority: 'high',
            estimatedTime: '1-2 days',
            difficulty: 'medium',
            status: 'in_progress',
            prerequisites: ['Find suitable housing'],
            resources: [
              { title: 'Utilities Setup Guide', url: '/guides/utilities', type: 'guide' }
            ],
            points: 30,
            icon: Zap
          }
        ]
      },
      {
        id: '3',
        title: 'Tax Preparation',
        description: 'Understand and prepare for U.S. tax obligations',
        category: 'taxes',
        completed: 0,
        total: 5,
        unlocked: true,
        icon: FileText,
        color: 'bg-green-500',
        items: [
          {
            id: '3-1',
            title: 'Get a Social Security Number',
            description: 'Apply for and receive your SSN for tax purposes',
            category: 'taxes',
            priority: 'high',
            estimatedTime: '2-4 weeks',
            difficulty: 'medium',
            status: 'pending',
            prerequisites: [],
            resources: [
              { title: 'SSN Application Guide', url: '/guides/ssn', type: 'guide' }
            ],
            points: 75,
            icon: FileText
          }
        ]
      },
      {
        id: '4',
        title: 'Career & Education',
        description: 'Advance your career and educational opportunities',
        category: 'career',
        completed: 0,
        total: 7,
        unlocked: false,
        icon: GraduationCap,
        color: 'bg-orange-500',
        items: []
      }
    ];

    setChecklists(mockChecklists);
  };

  const filterContent = () => {
    let filtered = [...checklists];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(checklist => 
        checklist.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        checklist.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(checklist => checklist.category === selectedCategory);
    }

    setFilteredChecklists(filtered);
  };

  const categories = [
    { id: 'all', name: 'All Categories', icon: Target, color: 'bg-gray-500' },
    { id: 'banking', name: 'Banking & Credit', icon: CreditCard, color: 'bg-blue-500' },
    { id: 'housing', name: 'Housing & Living', icon: Home, color: 'bg-purple-500' },
    { id: 'taxes', name: 'Taxes & Finance', icon: FileText, color: 'bg-green-500' },
    { id: 'career', name: 'Career & Education', icon: GraduationCap, color: 'bg-orange-500' },
    { id: 'insurance', name: 'Insurance', icon: Shield, color: 'bg-red-500' }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const totalActions = checklists.reduce((sum, checklist) => sum + checklist.total, 0);
  const completedActions = checklists.reduce((sum, checklist) => sum + checklist.completed, 0);
  const inProgressActions = checklists.reduce((sum, checklist) => 
    sum + checklist.items.filter(item => item.status === 'in_progress').length, 0);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          <CheckSquare className="w-10 h-10 inline-block mr-3 text-primary-600" />
          Next Steps
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Actionable checklists and practical tasks unlocked by your current stage. 
          Take control of your journey with clear, manageable steps.
        </p>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">
              <AnimatedCounter value={completedActions} />
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Completed</h3>
          <p className="text-sm text-gray-600">Tasks finished</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">
              <AnimatedCounter value={inProgressActions} />
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">In Progress</h3>
          <p className="text-sm text-gray-600">Currently working on</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-primary-100 rounded-lg">
              <Target className="w-6 h-6 text-primary-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">
              <AnimatedCounter value={totalActions - completedActions} />
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Remaining</h3>
          <p className="text-sm text-gray-600">Tasks to complete</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search checklists and tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="banking">Banking & Credit</option>
              <option value="housing">Housing & Living</option>
              <option value="taxes">Taxes & Finance</option>
              <option value="career">Career & Education</option>
              <option value="insurance">Insurance</option>
            </select>

            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Priorities</option>
              <option value="high">High Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="low">Low Priority</option>
            </select>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center px-4 py-2 rounded-full border transition-all duration-200 ${
              selectedCategory === category.id
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-600 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            <category.icon className="w-4 h-4 mr-2" />
            {category.name}
          </button>
        ))}
      </div>

      {/* Checklists */}
      <div className="space-y-6">
        {filteredChecklists.map((checklist) => (
          <div
            key={checklist.id}
            className={`bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden ${
              !checklist.unlocked ? 'opacity-60' : ''
            }`}
          >
            {/* Checklist Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-12 h-12 rounded-lg ${checklist.color} flex items-center justify-center mr-4`}>
                    <checklist.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{checklist.title}</h2>
                    <p className="text-gray-600">{checklist.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {checklist.completed}/{checklist.total}
                  </div>
                  <div className="text-sm text-gray-600">tasks completed</div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(checklist.completed / checklist.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Action Items */}
            <div className="p-6">
              <div className="space-y-4">
                {checklist.items.map((item) => (
                  <div
                    key={item.id}
                    className={`p-4 rounded-lg border transition-all duration-200 ${
                      item.status === 'completed' 
                        ? 'bg-green-50 border-green-200' 
                        : item.status === 'in_progress'
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-gray-50 border-gray-200 hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start flex-1">
                        <div className="mr-4 mt-1">
                          {item.status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : item.status === 'in_progress' ? (
                            <Clock className="w-5 h-5 text-blue-600" />
                          ) : (
                            <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                          <p className="text-gray-600 mb-3">{item.description}</p>
                          
                          {/* Tags */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                              {item.priority} priority
                            </span>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(item.difficulty)}`}>
                              {item.difficulty}
                            </span>
                            <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              {item.estimatedTime}
                            </span>
                            <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-600">
                              {item.points} points
                            </span>
                          </div>

                          {/* Resources */}
                          {item.resources.length > 0 && (
                            <div className="mb-3">
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Resources:</h4>
                              <div className="flex flex-wrap gap-2">
                                {item.resources.map((resource, index) => (
                                  <button
                                    key={index}
                                    onClick={() => navigate(resource.url)}
                                    className="text-sm text-primary-600 hover:text-primary-700 underline"
                                  >
                                    {resource.title}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Button */}
                      <button
                        onClick={() => {
                          if (item.status !== 'completed') {
                            // In a real app, this would update the task status
                            console.log('Toggle task:', item.id);
                          }
                        }}
                        className={`ml-4 px-4 py-2 rounded-lg font-medium transition-colors ${
                          item.status === 'completed'
                            ? 'bg-green-100 text-green-700 cursor-default'
                            : item.status === 'in_progress'
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-primary-600 text-white hover:bg-primary-700'
                        }`}
                      >
                        {item.status === 'completed' ? 'Completed' : 
                         item.status === 'in_progress' ? 'Continue' : 'Start'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredChecklists.length === 0 && (
        <div className="text-center py-12">
          <CheckSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No checklists found</h3>
          <p className="text-gray-600 mb-6">
            Try adjusting your search criteria or explore different categories.
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
              setSelectedPriority('all');
            }}
            className="btn btn-primary"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Call to Action */}
      <div className="mt-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-4">Ready to Take Action?</h2>
        <p className="text-lg mb-6 opacity-90">
          Every step you take brings you closer to your American dream. Start with high-priority tasks and build momentum!
        </p>
        <button
          onClick={() => navigate('/guides')}
          className="btn bg-white text-primary-600 hover:bg-gray-100"
        >
          Explore Learning Resources
        </button>
      </div>
    </div>
  );
};

export default NextStepsPage;
